import type { Announcements, UniqueIdentifier } from '@dnd-kit/core';
import { bt, btp } from '../../i18n';

/**
 * Screen-reader announcements for keyboard drag-and-drop (build-plan §7.4). dnd-kit pipes these into
 * its own polite live region; we phrase them in terms of human-readable section/block NAMES and 1-based
 * positions ("Hero grabbed, position 2 of 13") so a non-sighted seller can reorder by keyboard alone
 * (Space lifts → arrows move → Space drops → Esc cancels).
 *
 * `labelFor(id)` resolves an id to its display name (e.g. the registry `def.meta.label`); `total` is the
 * count of items in the list at the moment the message is built.
 */
export function buildAnnouncements(
  labelFor: (id: UniqueIdentifier) => string,
  positionOf: (id: UniqueIdentifier) => number,
  total: () => number,
  noun = 'section',
): Announcements {
  // The noun is a KEY (`section` | `block`), translated here with the rest of the sentence.
  const n = bt(`chrome.dnd.noun_${noun}`, noun);
  return {
    onDragStart({ active }) {
      return btp(
        'chrome.dnd.picked_up',
        'Picked up {noun} {name}. Position {pos} of {total}. Use the arrow keys to move, space to drop, escape to cancel.',
        { noun: n, name: labelFor(active.id), pos: positionOf(active.id), total: total() },
      );
    },
    onDragOver({ active, over }) {
      if (!over) return undefined;
      return btp('chrome.dnd.moved', '{noun} {name} moved to position {pos} of {total}.', {
        noun: n,
        name: labelFor(active.id),
        pos: positionOf(over.id),
        total: total(),
      });
    },
    onDragEnd({ active, over }) {
      if (!over) {
        return btp('chrome.dnd.dropped', '{noun} {name} dropped.', {
          noun: n,
          name: labelFor(active.id),
        });
      }
      return btp('chrome.dnd.dropped_at', '{noun} {name} dropped at position {pos} of {total}.', {
        noun: n,
        name: labelFor(active.id),
        pos: positionOf(over.id),
        total: total(),
      });
    },
    onDragCancel({ active }) {
      return btp(
        'chrome.dnd.cancelled',
        'Reordering cancelled. {noun} {name} returned to its place.',
        {
          noun: n,
          name: labelFor(active.id),
        },
      );
    },
  };
}
